import { DtoV1PropertyTaxEscrowInstallment } from '~/__generated__/api';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import { Chip } from '~/common/mui/Chip';
import { formatCurrency } from '~/common/util/format';

import { TAX_PAYMENT_TYPES } from '../constants';
import { formatTaxDate } from '../helpers';
import TaxSummaryItem from './TaxSummaryItem';

type PrimaryInstallmentsProps = {
  primaryInstallments: (DtoV1PropertyTaxEscrowInstallment & {
    isSupplemental?: boolean;
  })[];
  primaryInstallmentsTotal: number;
  isTaxEscrowWaived: boolean;
  nonAdValorum?: number | null;
};

export const PrimaryInstallments = ({
  primaryInstallments,
  primaryInstallmentsTotal,
  isTaxEscrowWaived,
  nonAdValorum = 0,
}: Readonly<PrimaryInstallmentsProps>) => {
  const nonAdValorumAmount = nonAdValorum ?? 0;

  return (
    <Stack sx={{ pl: '2rem', gap: 6 }}>
      <Stack sx={{ alignItems: 'flex-start' }}>
        <Text
          sx={{
            fontWeight: '600',
          }}
        >
          Tax Installments Breakdown
        </Text>
      </Stack>
      <Stack gap={3} sx={{ p: '0 2rem 0 2rem' }}>
        {primaryInstallments.map((installment) => {
          const isNotEscrowed =
            installment.paidType === TAX_PAYMENT_TYPES.NOT_ESCROWED;
          const pastBill =
            installment.paidType === TAX_PAYMENT_TYPES.PAST_BILL_PAY_AT_CLOSING;
          let badge: string | undefined;
          if (isNotEscrowed && isTaxEscrowWaived) {
            badge = 'Escrow Waiver';
          } else if (pastBill) {
            badge = '15% padding';
          } else {
            badge = undefined;
          }
          return (
            <TaxSummaryItem
              key={installment.installmentId}
              label={
                installment.isSupplemental
                  ? `${formatTaxDate(installment.distributionDate)} Supplemental`
                  : `${formatTaxDate(installment.distributionDate)}`
              }
              content={formatCurrency(
                isNotEscrowed && isTaxEscrowWaived
                  ? 0
                  : installment.installmentAmount,
              )}
              badge={badge}
            />
          );
        })}
        {nonAdValorumAmount > 0 && (
          <TaxSummaryItem
            label="Non-Ad Valorem"
            content={formatCurrency(nonAdValorumAmount)}
            data-testid="non-ad-valorem"
          />
        )}
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.5rem',
          }}
        >
          <Text
            sx={{
              fontSize: '1rem',
              fontWeight: '600',
            }}
          >
            Total:
          </Text>
          <Chip
            label={`= ${formatCurrency(primaryInstallmentsTotal)}`}
            sx={{
              borderRadius: '0.5rem',
              fontWeight: '600',
            }}
            color="success"
            data-testid="land-only-total"
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
